import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { LegalLinks } from "@/components/LegalLinks";

const SIDE = "https://images.unsplash.com/photo-1627308594190-a057cd4bfac8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function GoogleButton({ plan = null }) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  return (
    <>
      <GoogleSignInButton
        onSuccess={(u) => { setUser(u); nav(plan ? (u.onboarding_completed ? `/pricing?plan=${plan}` : `/onboarding?plan=${plan}`) : (u.onboarding_completed ? "/dashboard" : "/onboarding"), { replace: true }); }}
        onError={setErr}
      />
      {err && <p data-testid="google-auth-error" className="mt-2 text-sm text-destructive text-center">{err}</p>}
    </>
  );
}

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
            <span className="font-serif text-2xl font-semibold tracking-tight">CHENIA</span>
          </Link>
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-2 text-muted-foreground">{sub}</p>
          <div className="mt-8">{children}</div>
        </div>
        <LegalLinks className="mt-8 justify-center" testIdPrefix="auth" />
      </div>
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-chenia-espresso/20" />
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      nav(u.onboarding_completed ? "/dashboard" : "/onboarding", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Shell title={t("login_title")} sub={t("login_sub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">{t("email")}</Label>
          <Input id="email" data-testid="login-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="password">{t("password")}</Label>
          <Input id="password" data-testid="login-password" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
        </div>
        {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full h-11 rounded-full bg-primary hover:bg-chenia-terracottadark">
          {busy ? t("loading") : t("login")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("or")} <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton label={t("sign_in_google")} />
      <p className="mt-6 text-sm text-muted-foreground text-center">
        {t("no_account")}{" "}
        <Link to="/register" data-testid="go-register" className="font-semibold text-primary hover:underline">{t("register")}</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const requestedPlan = new URLSearchParams(loc.search).get("plan");
  const plan = requestedPlan === "plus" || requestedPlan === "premium" ? requestedPlan : null;
  const [form, setForm] = useState({ first_name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(form);
      nav(plan ? `/onboarding?plan=${plan}` : "/onboarding", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Shell title={t("register_title")} sub={t("register_sub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="fn">{t("first_name")}</Label>
          <Input id="fn" data-testid="register-firstname" required value={form.first_name}
            onChange={set("first_name")} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="em">{t("email")}</Label>
          <Input id="em" data-testid="register-email" type="email" required value={form.email}
            onChange={set("email")} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="pw">{t("password")}</Label>
          <Input id="pw" data-testid="register-password" type="password" required minLength={6} value={form.password}
            onChange={set("password")} className="mt-1.5 h-11 rounded-xl" />
        </div>
        {err && <p data-testid="register-error" className="text-sm text-destructive">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="register-submit"
          className="w-full h-11 rounded-full bg-primary hover:bg-chenia-terracottadark">
          {busy ? t("loading") : t("register")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("or")} <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton label={t("sign_in_google")} plan={plan} />
      <p className="mt-6 text-sm text-muted-foreground text-center">
        {t("have_account")}{" "}
        <Link to="/login" data-testid="go-login" className="font-semibold text-primary hover:underline">{t("login")}</Link>
      </p>
    </Shell>
  );
}
